/**
 * Назначение: сбор контекста для баг-репорта (клиентская часть).
 * Кто: staff SPA.
 * Внешнему ИИ этот контекст НЕ отправляется — только в тикет платформы.
 */

import { getDiagnosticsSnapshot } from './diagnostics-store';
import { getDeployedAppVersion } from '../config/app-version';

export type DiagnosticsAuthSnapshot = {
  user_id: number | null;
  role: string | null;
  org_id: number | null;
  branch_id: number | null;
  impersonation_org_id: number | null;
};

export type DiagnosticsAssistantSnapshot = {
  conversation_id: number | null;
  plan_id: number | null;
};

let authSnapshot: DiagnosticsAuthSnapshot = {
  user_id: null,
  role: null,
  org_id: null,
  branch_id: null,
  impersonation_org_id: null
};

let assistantSnapshot: DiagnosticsAssistantSnapshot = { conversation_id: null, plan_id: null };

export function setDiagnosticsAuthSnapshot(next: Partial<DiagnosticsAuthSnapshot>): void {
  authSnapshot = { ...authSnapshot, ...next };
}

export function setDiagnosticsAssistantSnapshot(next: Partial<DiagnosticsAssistantSnapshot>): void {
  assistantSnapshot = { ...assistantSnapshot, ...next };
}

function buildCommit(): string {
  return getDeployedAppVersion().commit ?? 'unknown';
}

function viewport(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  return { width: window.innerWidth || 0, height: window.innerHeight || 0 };
}

function timezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Снимок контекста без тел запросов и секретов. */
export function collectBugContext(overrides?: {
  route?: string;
  assistant?: Partial<DiagnosticsAssistantSnapshot>;
}): Record<string, unknown> {
  const diag = getDiagnosticsSnapshot();
  const assistant = { ...assistantSnapshot, ...(overrides?.assistant ?? {}) };
  const route =
    overrides?.route ??
    (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search ? '?…' : ''}` : '');
  const title = typeof document !== 'undefined' ? document.title : '';
  const vp = viewport();
  return {
    route,
    title,
    org_id: authSnapshot.org_id,
    branch_id: authSnapshot.branch_id,
    user_id: authSnapshot.user_id,
    role: authSnapshot.role,
    impersonation_org_id: authSnapshot.impersonation_org_id,
    build_commit: buildCommit(),
    app_version: getDeployedAppVersion(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport: vp,
    dpr: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    language: typeof navigator !== 'undefined' ? navigator.language : '',
    timezone: timezone(),
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    assistant: {
      conversation_id: assistant.conversation_id ?? null,
      plan_id: assistant.plan_id ?? null
    },
    api_failures: diag.api_failures,
    ui_errors: diag.ui_errors,
    routes: diag.routes
  };
}
